/**
 * Descriptor-field -> antd rendering. The generic pages drive every entity off its
 * FieldDef metadata, so a new descriptor renders list/show/create/edit with no per-entity
 * code. Mappings are intentionally lean; a descriptor that needs bespoke UI sets
 * `ui: { component }` and drops out of the generic path.
 */

import React from "react";
import { Form, Input, InputNumber, Switch, DatePicker, Tag } from "antd";
import dayjs from "dayjs";
import type { FieldDef, FieldType } from "@evo-flywheel/contracts";

const isLongText = (f: FieldDef) =>
  f.name === "description" || f.name === "content" || f.name.includes("content");

/** Render a value for a list column / show row. */
export function renderCell(field: FieldDef | undefined, value: unknown): React.ReactNode {
  if (value === null || value === undefined || value === "") return "—";
  const type: FieldType | undefined = field?.type;
  if (type === "boolean") {
    const on = value === true || value === "1" || value === 1;
    return <Tag color={on ? "green" : "default"}>{on ? "Sì" : "No"}</Tag>;
  }
  if (type === "datetime") return dayjs(value as string).format("YYYY-MM-DD HH:mm");
  if (type === "date") return dayjs(value as string).format("YYYY-MM-DD");
  if (type === "json") return <code>{JSON.stringify(value)}</code>;
  return String(value);
}

const dateValueProps = {
  getValueProps: (value: unknown) => ({
    value: value ? dayjs(value as string) : undefined,
  }),
};

/** Render an antd form control for a field. */
function control(field: FieldDef): React.ReactNode {
  switch (field.type) {
    case "boolean":
      return <Switch />;
    case "number":
      return <InputNumber style={{ width: "100%" }} precision={0} />;
    case "decimal":
      return <InputNumber style={{ width: "100%" }} precision={2} />;
    case "date":
      return <DatePicker style={{ width: "100%" }} />;
    case "datetime":
      return <DatePicker showTime style={{ width: "100%" }} />;
    case "json":
      return <Input.TextArea rows={4} />;
    case "ref":
      return <Input placeholder={`${field.refTo ?? "ref"} id`} />;
    default:
      return isLongText(field) ? <Input.TextArea rows={4} /> : <Input />;
  }
}

/** A single form item for a field, with required rule + date value coercion. */
export function FormField({
  field,
  required,
  initialValue,
}: {
  field: FieldDef;
  required?: boolean;
  initialValue?: string;
}): React.ReactElement {
  const isDate = field.type === "date" || field.type === "datetime";
  const format = field.type === "datetime" ? "YYYY-MM-DD HH:mm:ss" : "YYYY-MM-DD";
  return (
    <Form.Item
      name={field.name}
      label={field.label ?? field.name}
      valuePropName={field.type === "boolean" ? "checked" : undefined}
      initialValue={initialValue}
      rules={
        required
          ? [{ required: true, message: `${field.label ?? field.name} is required` }]
          : undefined
      }
      {...(isDate
        ? {
            ...dateValueProps,
            getValueFromEvent: (d: dayjs.Dayjs | null) =>
              d ? d.format(format) : null,
          }
        : {})}
    >
      {control(field)}
    </Form.Item>
  );
}
